import { FlowType, Step as ModelStep } from "@redotech/redo-model/return-flow";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import FlagIcon from "@redotech/redo-web/icon-old/flag.svg";
import { produce } from "immer";
import * as React from "react";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";

export interface FlagState {
  reason: string;
  next: StepId | undefined;
  customTitle?: string;
}

export const FLAG = (
  Details: React.ComponentType<StepTypeDetailsProps<FlagState>>,
): StepType<FlagState, ModelStep.Flag> => ({
  Details: (props: StepTypeDetailsProps<FlagState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Flag",
  valid(state, flowType) {
    return (
      state.next !== undefined ||
      flowType === FlowType.FINALIZE_RETURN ||
      flowType === FlowType.FINALIZE_CLAIM
    );
  },
  Icon: FlagIcon,
  iconColor: "success",
  description() {
    return "Prevent automatic processing";
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined, reason: "" },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(`Invalid handle index ${handleIndex} for Flag step`);
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.FLAG,
      next: state.next ? id(state.next) : undefined,
      reason: state.reason,
    };
  },
  fromModel(model, id) {
    return {
      customTitle: model.customTitle,
      next: model.next ? id(model.next) : undefined,
      reason: model.reason || "",
    };
  },
});
